import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getUserInfo, setUserInfo } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import './GettingStartedBanner.css';

interface SetupStatus {
  groups: number;
  members: number;
  eventTemplates: number;
  events: number;
  users: number;
}

interface SetupStep {
  key: string;
  labelKey: string;
  descKey: string;
  link: string;
  isComplete: boolean;
}

const GettingStartedBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, user } = useAuth();
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Check if user has dismissed the banner
  const userDismissed = user?.preferences?.dismissedOnboardingBanner;
  // Hide entirely when a super admin is impersonating — they're not really
  // onboarding this tenant, and the impersonated user's prefs shouldn't trigger
  // the banner for the superadmin's session.
  const isImpersonating = typeof window !== 'undefined' && localStorage.getItem('impersonating') === 'true';

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.organization.getSetupStatus();
      setStatus(data as SetupStatus);
    } catch {
      // Silently fail - banner just won't show
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin && !userDismissed && !dismissed && !isImpersonating) {
      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [isAdmin, userDismissed, dismissed, isImpersonating, fetchStatus]);

  const handleDismiss = async () => {
    setDismissed(true);
    // Persist in localStorage so it stays dismissed across refreshes
    const stored = getUserInfo();
    if (stored) {
      setUserInfo({
        ...stored,
        preferences: { ...(stored.preferences || {}), dismissedOnboardingBanner: true },
      });
    }
    try {
      await api.profile.update({
        preferences: { dismissedOnboardingBanner: true },
      } as any);
    } catch {
      // Ignore - banner is hidden locally anyway
    }
  };

  // Don't render for non-admins, dismissed, impersonating, or loading
  if (!isAdmin || userDismissed || dismissed || isImpersonating || loading || !status) {
    return null;
  }

  const steps: SetupStep[] = [
    {
      key: 'groups',
      labelKey: 'onboarding.stepGroups',
      descKey: 'onboarding.stepGroupsDesc',
      link: '/admin/groups/create',
      isComplete: status.groups > 0,
    },
    {
      key: 'members',
      labelKey: 'onboarding.stepMembers',
      descKey: 'onboarding.stepMembersDesc',
      link: '/admin/members/create',
      isComplete: status.members > 0,
    },
    {
      key: 'templates',
      labelKey: 'onboarding.stepTemplates',
      descKey: 'onboarding.stepTemplatesDesc',
      link: '/admin/event-templates/create',
      isComplete: status.eventTemplates > 0,
    },
    {
      key: 'events',
      labelKey: 'onboarding.stepEvents',
      descKey: 'onboarding.stepEventsDesc',
      link: '/admin/events/create',
      isComplete: status.events > 0,
    },
    {
      key: 'users',
      labelKey: 'onboarding.stepUsers',
      descKey: 'onboarding.stepUsersDesc',
      link: '/admin/users/create',
      isComplete: status.users > 1,
    },
  ];

  const incompleteSteps = steps.filter(s => !s.isComplete);
  const allComplete = incompleteSteps.length === 0;

  return (
    <div className="getting-started-banner">
      <div className="getting-started-banner__header">
        <div>
          <h3 className="getting-started-banner__title">
            {allComplete ? t('onboarding.allDone') : t('onboarding.gettingStarted')}
          </h3>
          <p className="getting-started-banner__desc">
            {allComplete ? t('onboarding.allDoneDesc') : t('onboarding.gettingStartedDesc')}
          </p>
        </div>
        <button
          className="getting-started-banner__dismiss"
          onClick={handleDismiss}
          title={t('onboarding.dismiss')}
        >
          &times;
        </button>
      </div>

      {!allComplete && (
        <div className="getting-started-banner__steps">
          {steps.map(step => (
            <Link
              key={step.key}
              to={step.link}
              className={`getting-started-banner__step ${step.isComplete ? 'getting-started-banner__step--done' : ''}`}
            >
              <span className="getting-started-banner__step-icon">
                {step.isComplete ? '✓' : '○'}
              </span>
              <span className="getting-started-banner__step-content">
                <span className="getting-started-banner__step-label">{t(step.labelKey)}</span>
                <span className="getting-started-banner__step-desc">{t(step.descKey)}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default GettingStartedBanner;
